import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useEffect, useState } from 'react';

interface UseDragSortingArgs<T>
  extends Readonly<{
    items: ReadonlyArray<T>;
    onReorder?: (reorderedItems: ReadonlyArray<T>) => void;
  }> {}

interface DragEndData<T>
  extends Readonly<{
    fromIndex: number;
    toIndex: number;
    items: ReadonlyArray<T>;
    reorderedItem: T | undefined;
  }> {}

/**
 * Custom hook to handle drag sorting of items
 * @param args - The arguments for the hook
 * @param args.items - The items to sort
 * @param args.onReorder - The function to call when the items are reordered
 * @returns The sorted items, whether the drag is active, and the handlers for drag start, cancel, and end
 */
export function useDragSorting<T extends Readonly<{ id: string }>>(args: UseDragSortingArgs<T>) {
  const { items, onReorder } = args;
  const [sortedItems, setSortedItems] = useState<ReadonlyArray<T>>(items);
  const [isDragActive, setIsDragActive] = useState(false);

  useEffect(() => {
    setSortedItems([...items]);
  }, [items]);

  const handleDragStart = useEventCallback(() => {
    setIsDragActive(true);
  });

  const handleDragCancel = useEventCallback(() => {
    setIsDragActive(false);
  });

  const handleDragEnd = useEventCallback((dragData: DragEndData<T>) => {
    setIsDragActive(false);

    const hasSortOrderChanged = !dragData.items.every((item, index) => item.id === sortedItems[index].id);

    if (!hasSortOrderChanged) return;

    setSortedItems(dragData.items);

    if (onReorder) {
      onReorder(dragData.items);
    }
  });

  return {
    sortedItems,
    isDragActive,
    handleDragStart,
    handleDragCancel,
    handleDragEnd,
  };
}
